import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, ReplaySubject, combineLatest, of } from "rxjs";
import { distinctUntilChanged, filter, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import { FireSubService } from "src/app/core/firebase/fireSub.service";
import { collection, collectionData, CollectionReference, Firestore, limit, orderBy, query } from "@angular/fire/firestore";
import { ClubStateDispatcher } from "src/app/core/state/club/club.dispatcher";
import { ClubMessage } from "src/app/core/state/club/club.state";
import { ClubService } from "src/app/core/firebase/club.service";

const DEFAULT_LIMIT = 11;

@Injectable()
export class ClubChatService {
    private _clubId: string;
    private _idSet = new ReplaySubject<string>(1);
    private _limitTo = new BehaviorSubject(DEFAULT_LIMIT);
    private _currentLimit: number = DEFAULT_LIMIT;
    private _limitHasChanged: boolean = false;
    private _stateClubId$: Observable<string>;
    private _clubMessage$: Observable<ClubMessage[]>;

    constructor(
        private fireSubService: FireSubService,
        private firestore: Firestore,
        private clubStateDispatcher: ClubStateDispatcher,
        private clubService: ClubService,
    ) {
        this.setStateClubId();
    }

    // set the current clubid for message retrival, triggers retrival if new id is different from the old one
    set clubId(id: string) {
        if (this.clubId === id) return;
        this._clubId = id;
        localStorage.setItem('selectedClubChat', id);
        this._currentLimit = DEFAULT_LIMIT;
        this._limitTo.next(DEFAULT_LIMIT);
        this._idSet.next(id);
    }

    // set the limit of messages that needs to be retrived, triggers new retrival if the new limit is different from the current one
    set limitTo(newLimitTo: number) {
        if (this._currentLimit === newLimitTo) {
            if (this._limitHasChanged === true) this._limitHasChanged = false;
            return;
        }
        this._currentLimit = newLimitTo;
        this._limitTo.next(this._currentLimit);
        this._limitHasChanged = true;
    }

    // observable for the current set clubId
    get idSet$() {
        return this._idSet.pipe(filter(id => id !== undefined), distinctUntilChanged())
    }

    // observable for club messages
    get clubMessage$() {
        if (this._clubMessage$ === undefined ||
            this._currentLimit !== DEFAULT_LIMIT && this._limitHasChanged) {
            this._clubMessage$ = this.getClubMessageObs();
            this._limitHasChanged = false;
        }
        return this._clubMessage$;
    }

    // set the state id from clubstate
    private setStateClubId() {
        this._stateClubId$ = this.clubStateDispatcher.getClubIdObs();
    }

    private getClubMessageObs(): Observable<ClubMessage[]> {
        return combineLatest([this.idSet$, this._stateClubId$, this._limitTo]).pipe(
            switchMap(([clubId, stateClubId, limitTo]) => {
                let clubMessage$: Observable<ClubMessage[]> = of([]);
                if (clubId === 'SOPO') return clubMessage$;
                if (clubId !== stateClubId) {
                    const collectionReference = collection(this.firestore, `clubs/${clubId}/messages`) as CollectionReference<ClubMessage>;
                    const collectionQuery = query(collectionReference, orderBy('dateCreated', 'desc'), limit(limitTo));
                    clubMessage$ = collectionData<ClubMessage>(collectionQuery, { idField: 'messageID' }).pipe(takeUntil(this.fireSubService.unsubscriber), shareReplay({ bufferSize: 1, refCount: true }));
                    return clubMessage$;
                }
                this.clubService.loadClubMessages(clubId, limitTo);
                return this.clubStateDispatcher.getClubMessages();
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        )
    }
}
